import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { ReadonlyRangePicker } from '../components/common/DatePickerControls'
import { PageLayout } from '../components/layout'
import dayjs from 'dayjs'
import {
    StatisticsHeader,
    StatisticsSummary,
    StatisticsChartArea,
    useStatisticsData,
    useComparisonData,
} from '../components/statistics'
import { DistributionChart } from '../components/charts'
import { ComparisonAnalysis } from '../components/analysis/ComparisonAnalysis/ComparisonAnalysis'
import {
    createStatisticsProjectReportExportJob,
    getStatisticsProjectReportExportJob,
    getStatisticsProjectReportExportJobDownload,
    listStatisticsProjectReportExportJobs,
    triggerStatisticsProjectReportExportDownload,
} from '../services/statisticsService'
import { usePermissions } from '../hooks/usePermissions'
import { getDevicePackageDisplayName } from '../utils/devicePackageDisplay'
import { DEVICE_PACKAGE_KEYS } from '../constants/devicePackages'
import { buildConfiguredPestTypeFilterOptions } from '../utils/mosquitoDisplay'
import { filterRegionSelectOption } from '../utils/regionSearch'
import {
    buildAllFilterOptions,
    getAllAwareDisplayValues,
    normalizeAllAwareValues,
} from '../components/statistics/filterSelectUtils'
import { reconcilePestTypeSelections } from './statisticsFilters'
import {
    PaginatedListCard,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../components/common/PaginatedListTable'
import type { Dayjs } from 'dayjs'
import type { RangePickerProps } from 'antd/es/date-picker'
import type { ColumnsType } from 'antd/es/table'
import type {
    StatisticsAvailableDateRange,
    StatisticsDataType,
    StatisticsDeviceOption,
    StatisticsExportJob,
    StatisticsExportJobStatus,
    StatisticsFilters,
    StatisticsProjectOption,
    StatisticsRegionOption,
    StatisticsSummaryDataType,
} from '../types/statistics'
import './Statistics.css'

const { Text, Title } = Typography

const AVAILABLE_CHART_TYPES = [
    'trend',
    'distribution',
    'prediction',
    'comparison',
] as const

type StatisticsChartType = (typeof AVAILABLE_CHART_TYPES)[number]
type QuickRangeKey =
    | 'all'
    | 'today'
    | 'yesterday'
    | 'lastWeek'
    | 'lastMonth'
    | 'lastHalfYear'
    | 'lastYear'

const DEFAULT_QUICK_RANGE_KEY: QuickRangeKey = 'all'
const FEATURE_MOSQUITO_SPECIES_RECOGNITION = 'mosquito_species_recognition'
const FEATURE_TREND_ANALYSIS = 'trend_analysis'
const FEATURE_COMPARISON_ANALYSIS = 'comparison_analysis'
const FEATURE_PREDICTION_ANALYSIS = 'prediction_analysis'
const FEATURE_MOSQUITO_DISTRIBUTION = 'mosquito_distribution'
const FEATURE_REPORT_EXPORT = 'report_export'
const LEGACY_DATA_REPORTS_FEATURE = 'data_reports'
const ANALYSIS_FEATURE_BY_CHART_TYPE: Record<StatisticsChartType, string> = {
    trend: FEATURE_TREND_ANALYSIS,
    distribution: FEATURE_MOSQUITO_SPECIES_RECOGNITION,
    prediction: FEATURE_PREDICTION_ANALYSIS,
    comparison: FEATURE_COMPARISON_ANALYSIS,
}
const LEGACY_COMPATIBLE_FEATURES: Record<string, string[]> = {
    [FEATURE_TREND_ANALYSIS]: [LEGACY_DATA_REPORTS_FEATURE],
    [FEATURE_COMPARISON_ANALYSIS]: [LEGACY_DATA_REPORTS_FEATURE],
    [FEATURE_PREDICTION_ANALYSIS]: [LEGACY_DATA_REPORTS_FEATURE],
    [FEATURE_MOSQUITO_DISTRIBUTION]: [
        LEGACY_DATA_REPORTS_FEATURE,
        FEATURE_MOSQUITO_SPECIES_RECOGNITION,
    ],
}
const EXPORT_TASKS_PAGE_SIZE_OPTIONS = ['5', '10', '20']

const QUICK_RANGE_KEYS: QuickRangeKey[] = [
    'all',
    'today',
    'yesterday',
    'lastWeek',
    'lastMonth',
    'lastHalfYear',
    'lastYear',
]

interface StatisticsProps {
    lockedChartType?: StatisticsChartType
}
const canAnalysisFeatureExport = (analysisFeature: string): boolean =>
    analysisFeature === FEATURE_TREND_ANALYSIS

const getStatisticsExportPermission = (): string => 'statistics:trend:export'

const getAnalysisPageTitleKey = (chartType?: StatisticsChartType) => {
    if (chartType === 'distribution') {
        return {
            titleKey: 'pages.speciesAnalysis.title',
            defaultTitle: '蚊虫种类分析',
            breadcrumbKey: 'breadcrumb.speciesAnalysis',
        }
    }

    if (chartType === 'trend') {
        return {
            titleKey: 'pages.trendAnalysis.title',
            defaultTitle: '蚊虫趋势分析',
            breadcrumbKey: 'breadcrumb.trendAnalysis',
        }
    }

    return {
        titleKey: 'title',
        defaultTitle: undefined,
        breadcrumbKey: 'navigation:breadcrumb.statistics',
    }
}

const FIXED_APPLICABLE_VERSION_KEYS_BY_ANALYSIS: Partial<
    Record<StatisticsChartType, string[]>
> = {
    distribution: ['recognition', 'insight'],
    trend: ['insight'],
}

const getFixedApplicableVersionNames = (
    chartType: StatisticsChartType,
    t: ReturnType<typeof useTranslation>['t']
) => {
    const versionKeys = FIXED_APPLICABLE_VERSION_KEYS_BY_ANALYSIS[chartType] ?? []

    return versionKeys
        .map((versionKey) =>
            getDevicePackageDisplayName(versionKey, versionKey, t)
        )
        .join('、')
}

const buildVersionCountSummary = (
    devices: StatisticsDeviceOption[],
    t: ReturnType<typeof useTranslation>['t']
) => {
    if (devices.length === 0) {
        return '-'
    }

    const unsetVersionLabel = t('analysisHeader.unsetVersion', {
        defaultValue: '未设置',
    })

    const packageOrderByLabel = new Map(
        DEVICE_PACKAGE_KEYS.map((packageKey, index) => [
            getDevicePackageDisplayName(packageKey, packageKey, t),
            index,
        ])
    )
    const versionCounts = devices.reduce<
        Record<string, { count: number; order: number; firstIndex: number }>
    >((accumulator, device, index) => {
        const versionLabel =
            'displayPurchaseVersion' in device
                ? device.displayPurchaseVersion?.trim() || unsetVersionLabel
                : getDevicePackageDisplayName(
                      device.packageKey || 'unknown',
                      device.packageKey || 'unknown',
                      t
                  )
        const packageIndex = DEVICE_PACKAGE_KEYS.indexOf(
            device.packageKey as (typeof DEVICE_PACKAGE_KEYS)[number]
        )
        const order =
            packageIndex >= 0
                ? packageIndex
                : packageOrderByLabel.get(versionLabel) ?? Number.MAX_SAFE_INTEGER
        const existing = accumulator[versionLabel]
        accumulator[versionLabel] = existing
            ? { ...existing, count: existing.count + 1 }
            : { count: 1, order, firstIndex: index }
        return accumulator
    }, {})

    return Object.entries(versionCounts)
        .sort(([, left], [, right]) =>
            left.order - right.order || left.firstIndex - right.firstIndex
        )
        .map(([versionName, { count }]) =>
            t('analysisHeader.packageCount', {
                package: versionName,
                count,
                defaultValue: `${versionName} ${count} 台`,
            })
        )
        .join('，')
}

const hasDisplayPurchaseVersion = (device: StatisticsDeviceOption) =>
    Boolean(device.displayPurchaseVersion?.trim())

const collectProjectIdsFromDevices = (devices: StatisticsDeviceOption[]) => {
    const projectIds = new Set<number>()
    devices.forEach((device) => {
        if (device.projectId != null) {
            projectIds.add(device.projectId)
        }
        const projectPathIds = device.projectPathIds ?? []
        projectPathIds.forEach((projectId) => {
            projectIds.add(projectId)
        })
    })
    return projectIds
}

const collectRegionIdsFromDevices = (devices: StatisticsDeviceOption[]) => {
    const regionIds = new Set<number>()
    devices.forEach((device) => {
        if (device.regionId != null) {
            regionIds.add(device.regionId)
        }
        device.regionPathIds.forEach((regionId) => {
            regionIds.add(regionId)
        })
    })
    return regionIds
}

const filterProjectOptionsByDevices = (
    projectOptions: StatisticsProjectOption[],
    devices: StatisticsDeviceOption[],
    enabled: boolean
) => {
    if (!enabled) {
        return projectOptions
    }

    const projectIds = collectProjectIdsFromDevices(devices)
    return projectOptions.filter((project) => projectIds.has(project.id))
}

const filterRegionOptionsByDevices = (
    regionOptions: StatisticsRegionOption[],
    devices: StatisticsDeviceOption[],
    enabled: boolean
) => {
    if (!enabled) {
        return regionOptions
    }

    const regionIds = collectRegionIdsFromDevices(devices)
    return regionOptions.filter((region) => regionIds.has(region.id))
}

const mapDistributionToComparisonRows = (
    distribution: Array<{ name: string; value: number }> | undefined
) =>
    (distribution ?? []).map((item) => ({
        name: item.name,
        count: item.value,
    }))

const buildComparisonCardData = (
    summary: StatisticsSummaryDataType | null | undefined
) => {
    const distribution = summary?.pestDistribution ?? []

    return {
        total: distribution.reduce((total, item) => total + item.value, 0),
        byType: mapDistributionToComparisonRows(distribution),
    }
}

const getSpeciesSummaryForChart = (
    data: StatisticsDataType | StatisticsSummaryDataType
): StatisticsSummaryDataType =>
    (data as StatisticsDataType).speciesSummary ?? data

const getQuickDateRange = (
    quickRangeKey: QuickRangeKey,
    businessDate?: string | null
): [Dayjs, Dayjs] => {
    const anchorDate = businessDate ? dayjs(businessDate) : dayjs()
    const anchorStart = anchorDate.startOf('day')
    const anchorEnd = anchorStart.endOf('day')

    switch (quickRangeKey) {
        case 'all':
            return [anchorStart, anchorEnd]
        case 'today':
            return [anchorStart, anchorEnd]
        case 'yesterday': {
            const yesterdayStart = anchorStart.subtract(1, 'day')
            return [yesterdayStart, yesterdayStart.endOf('day')]
        }
        case 'lastWeek':
            return [anchorStart.subtract(6, 'day'), anchorEnd]
        case 'lastMonth':
            return [anchorStart.subtract(1, 'month'), anchorEnd]
        case 'lastHalfYear':
            return [anchorStart.subtract(6, 'month'), anchorEnd]
        case 'lastYear':
            return [anchorStart.subtract(1, 'year'), anchorEnd]
    }
}

const buildDateRangeFromAvailableRange = (
    availableDateRange: StatisticsAvailableDateRange
): [Dayjs, Dayjs] => {
    if (availableDateRange.startDate && availableDateRange.endDate) {
        return [
            dayjs(availableDateRange.startDate).startOf('day'),
            dayjs(availableDateRange.endDate).endOf('day'),
        ]
    }

    return [
        dayjs(availableDateRange.startTime),
        dayjs(availableDateRange.endTime),
    ]
}

const getMatchingQuickRangeKey = (
    dateRange: [Dayjs, Dayjs],
    availableDateRange: StatisticsAvailableDateRange | null,
    businessDate?: string | null
): QuickRangeKey | null => {
    const [startDate, endDate] = dateRange

    if (availableDateRange) {
        if (
            availableDateRange.startDate &&
            availableDateRange.endDate &&
            startDate.format('YYYY-MM-DD') === availableDateRange.startDate &&
            endDate.format('YYYY-MM-DD') === availableDateRange.endDate
        ) {
            return 'all'
        }

        if (
            !availableDateRange.startDate &&
            !availableDateRange.endDate &&
            startDate.valueOf() === availableDateRange.startTime &&
            endDate.valueOf() === availableDateRange.endTime
        ) {
            return 'all'
        }
    }

    return (
        QUICK_RANGE_KEYS.find((quickRangeKey) => {
            if (quickRangeKey === 'all') {
                return false
            }
            const [quickStartDate, quickEndDate] =
                getQuickDateRange(quickRangeKey, businessDate)
            return (
                startDate.isSame(quickStartDate, 'day') &&
                endDate.isSame(quickEndDate, 'day')
            )
        }) ?? null
    )
}

const createEmptyFilters = (): StatisticsFilters => ({
    projectIds: [],
    regionIds: [],
    deviceIds: [],
    pestTypes: [],
    deviceScope: 'package_visible',
})

const createInitialRangeState = () => {
    const dateRange = getQuickDateRange('today')

    return {
        dateRange,
        requestedDateRange: null,
        activeQuickRangeKey: DEFAULT_QUICK_RANGE_KEY,
    }
}

const cloneFilters = (filters: StatisticsFilters): StatisticsFilters => ({
    ...filters,
    projectIds: [...filters.projectIds],
    regionIds: [...filters.regionIds],
    deviceIds: [...filters.deviceIds],
    pestTypes: [...filters.pestTypes],
    deviceScope: filters.deviceScope,
})

const areArraysEqual = (
    left: Array<number | string>,
    right: Array<number | string>
) => left.length === right.length && left.every((value, index) => value === right[index])

export const getAvailableDeviceOptions = (
    filters: StatisticsFilters,
    deviceOptions: StatisticsDeviceOption[],
    projectOptions: StatisticsProjectOption[] = []
) => {
    const projectOptionById = new Map(
        projectOptions.map((project) => [project.id, project])
    )

    return deviceOptions.filter((device) => {
        const matchesSelectedProjects =
            filters.projectIds.length === 0 ||
            filters.projectIds.some((projectId) => {
                const includeDescendants =
                    projectOptionById.get(projectId)?.includeDescendants ?? true

                if (includeDescendants) {
                    return (
                        (device.projectPathIds ?? []).includes(projectId) ||
                        device.projectId === projectId
                    )
                }

                return device.projectId === projectId
            })

        if (
            filters.projectIds.length > 0 &&
            !matchesSelectedProjects
        ) {
            return false
        }
        if (filters.regionIds.length === 0) {
            return true
        }

        return device.regionPathIds.some((regionId) =>
            filters.regionIds.includes(regionId)
        )
    })
}

const hasCapability = (
    device: StatisticsDeviceOption,
    featureKey: string
): boolean => {
    if (device.capabilityKeys.includes(featureKey)) {
        return true
    }
    return (LEGACY_COMPATIBLE_FEATURES[featureKey] ?? []).some((legacyFeature) =>
        device.capabilityKeys.includes(legacyFeature)
    )
}

const hasConfiguredCapability = (
    device: StatisticsDeviceOption,
    featureKey: string
): boolean => device.capabilityKeys.includes(featureKey)

const getCapabilityVersionName = (
    statisticsData: StatisticsDataType | null,
    featureKey: string,
    t: ReturnType<typeof useTranslation>['t']
): string | undefined => {
    const names = (statisticsData?.deviceVersionDetections || [])
        .filter((item) =>
            hasCapability(
                {
                    id: item.key,
                    label: item.name,
                    regionPathIds: [],
                    capabilityKeys: item.capabilityKeys ?? [],
                },
                featureKey
            )
        )
        .map((item) => getDevicePackageDisplayName(item.key, item.name, t))

    return names.length > 0 ? names.join(' ') : undefined
}

const Statistics: React.FC<StatisticsProps> = ({ lockedChartType }) => {
    const { t } = useTranslation(['statistics', 'device', 'common'])
    const { hasPermission } = usePermissions()
    const isDedicatedAnalysisPage = lockedChartType !== undefined
    const analysisPageKey = lockedChartType ?? 'overview'

    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() =>
        createInitialRangeState().dateRange
    )
    const [requestedDateRange, setRequestedDateRange] = useState<
        [Dayjs, Dayjs] | null
    >(() =>
        createInitialRangeState().requestedDateRange
    )
    const [activeQuickRangeKey, setActiveQuickRangeKey] =
        useState<QuickRangeKey | null>(
            createInitialRangeState().activeQuickRangeKey
        )
    const [allAvailableDateRange, setAllAvailableDateRange] =
        useState<StatisticsAvailableDateRange | null>(null)
    const [chartType, setChartType] = useState<StatisticsChartType>('trend')
    const [filters, setFilters] = useState<StatisticsFilters>(() =>
        createEmptyFilters()
    )
    const [exportModalOpen, setExportModalOpen] = useState(false)
    const [exportDateRange, setExportDateRange] = useState<[Dayjs, Dayjs]>(() => [
        dayjs().subtract(30, 'days'),
        dayjs(),
    ])
    const [exportFilters, setExportFilters] = useState<StatisticsFilters>(() =>
        createEmptyFilters()
    )
    const [exporting, setExporting] = useState(false)
    const exportSubmittingRef = useRef(false)
    const [exportJobs, setExportJobs] = useState<StatisticsExportJob[]>([])
    const [exportJobsVisible, setExportJobsVisible] = useState(false)
    const [exportJobsLoading, setExportJobsLoading] = useState(false)
    const [exportJobsError, setExportJobsError] = useState<string | null>(null)
    const [exportJobsPage, setExportJobsPage] = useState(1)
    const [exportJobsPageSize, setExportJobsPageSize] = useState(5)
    const [activeExportJobId, setActiveExportJobId] = useState<string | null>(null)
    const [refreshToken, setRefreshToken] = useState(0)
    const previousAnalysisPageKeyRef = useRef(analysisPageKey)
    const activeChartType = lockedChartType ?? chartType
    const currentAnalysisFeature = ANALYSIS_FEATURE_BY_CHART_TYPE[activeChartType]
    const canExportStatistics =
        canAnalysisFeatureExport(currentAnalysisFeature) &&
        hasPermission(getStatisticsExportPermission())
    const usesSummaryDataForChart =
        currentAnalysisFeature === FEATURE_MOSQUITO_SPECIES_RECOGNITION ||
        currentAnalysisFeature === FEATURE_TREND_ANALYSIS
    const comparisonDateRange = requestedDateRange ?? dateRange
    const selectedRangeDays =
        comparisonDateRange[1].diff(comparisonDateRange[0], 'day') + 1
    const trendComparisonWindowDays = Math.min(
        7,
        Math.max(1, selectedRangeDays)
    )

    const {
        loading: summaryLoading,
        statisticsData: summaryData,
        availableDateRange,
        businessDate,
        error: summaryError,
        projectOptions,
        regionOptions,
        deviceOptions,
        pestTypeOptions,
    } = useStatisticsData(
        requestedDateRange,
        filters,
        true,
        refreshToken,
        isDedicatedAnalysisPage ? currentAnalysisFeature : undefined
    )
    const {
        loading: chartLoading,
        statisticsData: requestedChartData,
    } = useStatisticsData(
        requestedDateRange ?? dateRange,
        filters,
        !usesSummaryDataForChart,
        refreshToken,
        currentAnalysisFeature
    )
    const shouldLoadComparisonData =
        !summaryLoading &&
        !!summaryData &&
        (activeChartType === 'comparison' ||
            activeChartType === 'trend' ||
            (isDedicatedAnalysisPage && activeChartType === 'distribution'))

    const { comparisonData } = useComparisonData(
        requestedDateRange ?? dateRange,
        filters,
        shouldLoadComparisonData,
        refreshToken,
        currentAnalysisFeature,
        activeChartType === 'trend' ? trendComparisonWindowDays : undefined
    )
    const statisticsData = summaryData
    const bundledOperationalAnalysisData =
        summaryData?.analysisSummaries?.[currentAnalysisFeature] ??
        summaryData?.analysisSummaries?.[FEATURE_TREND_ANALYSIS]
    const bundledChartData =
        currentAnalysisFeature === FEATURE_MOSQUITO_SPECIES_RECOGNITION
            ? summaryData
            : bundledOperationalAnalysisData ?? summaryData
    const chartData = usesSummaryDataForChart
        ? bundledChartData ?? null
        : requestedChartData
    const isChartLoading = !usesSummaryDataForChart && chartLoading
    const chartSpeciesSummary: StatisticsSummaryDataType | undefined =
        chartData && 'speciesSummary' in chartData
            ? (chartData as StatisticsDataType).speciesSummary
            : undefined
    const summaryErrorMessage = t('common:messages.serverError')

    const {
        loading: exportOptionsLoading,
        projectOptions: exportProjectOptions,
        regionOptions: exportRegionOptions,
        deviceOptions: exportDeviceOptions,
        pestTypeOptions: exportPestTypeOptions,
    } = useStatisticsData(
        exportDateRange,
        exportFilters,
        exportModalOpen,
        0,
        currentAnalysisFeature
    )

    useEffect(() => {
        if (previousAnalysisPageKeyRef.current === analysisPageKey) {
            return
        }

        previousAnalysisPageKeyRef.current = analysisPageKey
        const initialRangeState = createInitialRangeState()

        setFilters(createEmptyFilters())
        setAllAvailableDateRange(null)
        setDateRange(initialRangeState.dateRange)
        setRequestedDateRange(initialRangeState.requestedDateRange)
        setActiveQuickRangeKey(initialRangeState.activeQuickRangeKey)
        setExportModalOpen(false)
        setExportJobsVisible(false)
        setActiveExportJobId(null)
        setRefreshToken((current) => current + 1)
    }, [analysisPageKey])

    useEffect(() => {
        if (!availableDateRange) {
            return
        }

        setAllAvailableDateRange(availableDateRange)
        if (requestedDateRange !== null) {
            return
        }

        const [availableRangeStart, availableRangeEnd] =
            buildDateRangeFromAvailableRange(availableDateRange)
        if (
            dateRange[0].isSame(availableRangeStart) &&
            dateRange[1].isSame(availableRangeEnd)
        ) {
            return
        }

        setDateRange(buildDateRangeFromAvailableRange(availableDateRange))
    }, [
        availableDateRange,
        dateRange,
        requestedDateRange,
    ])

    const hasActiveFilters =
        filters.projectIds.length > 0 ||
        filters.regionIds.length > 0 ||
        filters.deviceIds.length > 0 ||
        filters.pestTypes.length > 0

    const isEmptyResult =
        !!chartData &&
        chartData.totalDetections === 0 &&
        chartData.uniquePestTypes === 0 &&
        (chartSpeciesSummary?.totalDetections ?? 0) === 0 &&
        (chartSpeciesSummary?.uniquePestTypes ?? 0) === 0
    const speciesRecognitionVersionName = useMemo(
        () =>
            getCapabilityVersionName(
                summaryData,
                FEATURE_MOSQUITO_SPECIES_RECOGNITION,
                t
            ),
        [summaryData, t]
    )
    const dataReportsVersionName = useMemo(
        () => getCapabilityVersionName(chartData, currentAnalysisFeature, t),
        [chartData, currentAnalysisFeature, t]
    )

    const featureScopedDeviceOptions = useMemo(
        () =>
            isDedicatedAnalysisPage
                ? deviceOptions.filter((device) =>
                      hasConfiguredCapability(device, currentAnalysisFeature)
                  )
                : deviceOptions,
        [currentAnalysisFeature, deviceOptions, isDedicatedAnalysisPage]
    )
    const filterProjectOptions = useMemo(
        () =>
            filterProjectOptionsByDevices(
                projectOptions,
                featureScopedDeviceOptions,
                isDedicatedAnalysisPage
            ),
        [featureScopedDeviceOptions, isDedicatedAnalysisPage, projectOptions]
    )
    const filterRegionOptions = useMemo(
        () =>
            filterRegionOptionsByDevices(
                regionOptions,
                featureScopedDeviceOptions,
                isDedicatedAnalysisPage
            ),
        [featureScopedDeviceOptions, isDedicatedAnalysisPage, regionOptions]
    )
    const availableDeviceOptions = useMemo(
        () =>
            getAvailableDeviceOptions(
                filters,
                featureScopedDeviceOptions,
                filterProjectOptions
            ),
        [featureScopedDeviceOptions, filterProjectOptions, filters]
    )
    const statisticsScopeDeviceOptions = useMemo(
        () =>
            getAvailableDeviceOptions(
                filters,
                deviceOptions,
                projectOptions
            ),
        [deviceOptions, filters, projectOptions]
    )
    const currentScopedDeviceOptions = useMemo(
        () =>
            filters.deviceIds.length > 0
                ? availableDeviceOptions.filter((device) =>
                      filters.deviceIds.includes(device.id)
                  )
                : availableDeviceOptions,
        [availableDeviceOptions, filters.deviceIds]
    )
    const hasExportableDevices = featureScopedDeviceOptions.some((device) =>
        hasConfiguredCapability(device, FEATURE_REPORT_EXPORT)
    )
    const canConfirmExport =
        exportDateRange[0] !== null && exportDateRange[1] !== null
    const exportDisabledReason = hasExportableDevices
        ? undefined
        : t('export.unsupported')
    const displayPestTypeOptions = useMemo(
        () => buildConfiguredPestTypeFilterOptions(pestTypeOptions),
        [pestTypeOptions]
    )
    const exportDisplayPestTypeOptions = useMemo(
        () => buildConfiguredPestTypeFilterOptions(exportPestTypeOptions),
        [exportPestTypeOptions]
    )
    const exportFeatureScopedDeviceOptions = useMemo(
        () =>
            exportDeviceOptions.filter((device) =>
                hasConfiguredCapability(device, currentAnalysisFeature)
            ),
        [currentAnalysisFeature, exportDeviceOptions]
    )
    const exportProjectOptionsForModal = useMemo(
        () =>
            filterProjectOptionsByDevices(
                exportProjectOptions,
                exportFeatureScopedDeviceOptions,
                true
            ),
        [exportFeatureScopedDeviceOptions, exportProjectOptions]
    )
    const exportProjectSelectOptions = exportProjectOptionsForModal.map(
        (project) => ({
            value: project.id,
            label: project.label,
        })
    )
    const exportRegionSelectOptions = exportRegionOptions.map((region) => ({
        value: region.id,
        label: region.label,
    }))
    const exportAvailableDeviceOptions = useMemo(
        () =>
            getAvailableDeviceOptions(
                exportFilters,
                exportFeatureScopedDeviceOptions,
                exportProjectOptionsForModal
            ),
        [
            exportFeatureScopedDeviceOptions,
            exportFilters,
            exportProjectOptionsForModal,
        ]
    )
    const exportDeviceSelectOptions = exportAvailableDeviceOptions.map((device) => ({
        value: device.id,
        label: device.label,
    }))
    useEffect(() => {
        setFilters((previousFilters) => {
            const availableRegionIds = new Set(
                filterRegionOptions.map((region) => region.id)
            )
            const availableProjectIds = new Set(
                filterProjectOptions.map((project) => project.id)
            )
            const availableDeviceIds = new Set(
                availableDeviceOptions.map((device) => device.id)
            )
            const nextFilters = {
                ...previousFilters,
                projectIds: previousFilters.projectIds.filter((projectId) =>
                    availableProjectIds.has(projectId)
                ),
                regionIds: previousFilters.regionIds.filter((regionId) =>
                    availableRegionIds.has(regionId)
                ),
                deviceIds: previousFilters.deviceIds.filter((deviceId) =>
                    availableDeviceIds.has(deviceId)
                ),
                pestTypes: reconcilePestTypeSelections(
                    previousFilters.pestTypes,
                    displayPestTypeOptions
                ),
            }

            if (
                areArraysEqual(previousFilters.projectIds, nextFilters.projectIds) &&
                areArraysEqual(previousFilters.regionIds, nextFilters.regionIds) &&
                areArraysEqual(previousFilters.deviceIds, nextFilters.deviceIds) &&
                areArraysEqual(previousFilters.pestTypes, nextFilters.pestTypes)
            ) {
                return previousFilters
            }

            return nextFilters
        })
    }, [
        availableDeviceOptions,
        displayPestTypeOptions,
        filterProjectOptions,
        filterRegionOptions,
    ])

    useEffect(() => {
        if (!exportModalOpen) {
            return
        }

        setExportFilters((previousFilters) => {
            const availableProjectIds = new Set(
                exportProjectOptionsForModal.map((project) => project.id)
            )
            const availableRegionIds = new Set(
                exportRegionOptions.map((region) => region.id)
            )
            const nextFiltersWithoutDevices = {
                ...previousFilters,
                projectIds: previousFilters.projectIds.filter((projectId) =>
                    availableProjectIds.has(projectId)
                ),
                regionIds: previousFilters.regionIds.filter((regionId) =>
                    availableRegionIds.has(regionId)
                ),
                deviceIds: previousFilters.deviceIds,
                pestTypes: reconcilePestTypeSelections(
                    previousFilters.pestTypes,
                    exportDisplayPestTypeOptions
                ),
            }
            const availableDeviceIds = new Set(
                getAvailableDeviceOptions(
                    nextFiltersWithoutDevices,
                    exportFeatureScopedDeviceOptions,
                    exportProjectOptionsForModal
                ).map((device) => device.id)
            )
            const nextFilters = {
                ...nextFiltersWithoutDevices,
                deviceIds: previousFilters.deviceIds.filter((deviceId) =>
                    availableDeviceIds.has(deviceId)
                ),
            }

            if (
                areArraysEqual(previousFilters.projectIds, nextFilters.projectIds) &&
                areArraysEqual(previousFilters.regionIds, nextFilters.regionIds) &&
                areArraysEqual(previousFilters.deviceIds, nextFilters.deviceIds) &&
                areArraysEqual(previousFilters.pestTypes, nextFilters.pestTypes)
            ) {
                return previousFilters
            }

            return nextFilters
        })
    }, [
        exportModalOpen,
        exportDisplayPestTypeOptions,
        exportFeatureScopedDeviceOptions,
        exportProjectOptionsForModal,
        exportRegionOptions,
    ])

    const handleChartTypeChange = useCallback((nextChartType: string) => {
        if (lockedChartType) {
            return
        }

        setChartType(
            AVAILABLE_CHART_TYPES.includes(nextChartType as StatisticsChartType)
                ? (nextChartType as StatisticsChartType)
                : 'trend'
        )
    }, [lockedChartType])

    const handleDateRangeChange = useCallback<
        NonNullable<RangePickerProps['onChange']>
    >((dates) => {
        if (!dates || !dates[0] || !dates[1]) {
            return
        }

        const [startDate, endDate] = dates as [Dayjs, Dayjs]
        setActiveQuickRangeKey(
            getMatchingQuickRangeKey(
                [startDate, endDate],
                allAvailableDateRange,
                businessDate
            )
        )
        setRequestedDateRange([startDate, endDate])
        setDateRange((previousRange) => {
            if (
                previousRange[0].isSame(startDate) &&
                previousRange[1].isSame(endDate)
            ) {
                return previousRange
            }

            return [startDate, endDate]
        })
    }, [allAvailableDateRange, businessDate])

    const handleQuickRangeChange = useCallback((quickRangeKey: string) => {
        if (!QUICK_RANGE_KEYS.includes(quickRangeKey as QuickRangeKey)) {
            return
        }

        if (quickRangeKey !== 'all' && !businessDate) {
            return
        }

        if (quickRangeKey === 'all') {
            setActiveQuickRangeKey('all')
            setRequestedDateRange(null)
            if (allAvailableDateRange) {
                setDateRange(
                    buildDateRangeFromAvailableRange(allAvailableDateRange)
                )
            }
            return
        }

        const selectedQuickRangeKey = quickRangeKey as QuickRangeKey
        const nextDateRange = getQuickDateRange(
            selectedQuickRangeKey,
            businessDate
        )
        setActiveQuickRangeKey(selectedQuickRangeKey)
        setRequestedDateRange(nextDateRange)
        setDateRange(nextDateRange)
    }, [allAvailableDateRange, businessDate])

    const updateFilter = useCallback(
        <K extends keyof StatisticsFilters>(
            filterKey: K,
            values: StatisticsFilters[K]
        ) => {
            setFilters((previousFilters) => {
                if (
                    areArraysEqual(
                        previousFilters[filterKey] as Array<number | string>,
                        values as Array<number | string>
                    )
                ) {
                    return previousFilters
                }

                return {
                    ...previousFilters,
                    [filterKey]: values,
                }
            })
        },
        []
    )

    const handleResetFilters = useCallback(() => {
        setFilters(createEmptyFilters())
        setActiveQuickRangeKey('all')
        setRequestedDateRange(null)
        if (allAvailableDateRange) {
            setDateRange(buildDateRangeFromAvailableRange(allAvailableDateRange))
        }
    }, [allAvailableDateRange])

    const handleRefresh = useCallback(() => {
        setRefreshToken((current) => current + 1)
    }, [])

    const handleOpenExportModal = useCallback(() => {
        if (!canExportStatistics) {
            return
        }
        if (!hasExportableDevices) {
            message.warning(t('export.unsupported'))
            return
        }
        setExportDateRange([dateRange[0], dateRange[1]])
        setExportFilters(cloneFilters(filters))
        setExportModalOpen(true)
    }, [canExportStatistics, dateRange, filters, hasExportableDevices, t])

    const refreshExportJobs = useCallback(async () => {
        if (!canExportStatistics) {
            return
        }
        setExportJobsLoading(true)
        try {
            const result = await listStatisticsProjectReportExportJobs()
            if (result.success) {
                setExportJobs(result.jobs)
                setExportJobsError(null)
            } else {
                setExportJobsError(result.errorMessage || t('exportTasks.loadFailed'))
            }
        } finally {
            setExportJobsLoading(false)
        }
    }, [canExportStatistics, t])

    const handleOpenExportJobs = useCallback(() => {
        setExportModalOpen(false)
        setExportJobsVisible(true)
        void refreshExportJobs()
    }, [refreshExportJobs])

    const handleExport = useCallback(async () => {
        if (!canConfirmExport || exportSubmittingRef.current) {
            return
        }
        exportSubmittingRef.current = true
        setExporting(true)
        try {
            const result = await createStatisticsProjectReportExportJob({
                project_ids: exportFilters.projectIds,
                region_ids: exportFilters.regionIds,
                device_ids: exportFilters.deviceIds,
                pest_types: exportFilters.pestTypes,
                start_date: exportDateRange[0].format('YYYY-MM-DD'),
                end_date: exportDateRange[1].format('YYYY-MM-DD'),
                analysis_feature: currentAnalysisFeature,
            })
            if (result.success && result.job) {
                setExportJobs((current) => [
                    result.job!,
                    ...current.filter((job) => job.id !== result.job!.id),
                ])
                setActiveExportJobId(result.job.id)
                setExportModalOpen(false)
                message.success(t('export.taskCreated'))
            } else {
                message.error(result.errorMessage || t('export.failed'))
            }
        } catch (error) {
            console.error('Failed to export statistics data:', error)
            message.error(t('export.failed'))
        } finally {
            exportSubmittingRef.current = false
            setExporting(false)
        }
    }, [
        canConfirmExport,
        currentAnalysisFeature,
        exportDateRange,
        exportFilters,
        t,
    ])

    const handleExportJobDownload = useCallback(
        async (jobId: string) => {
            const result = await getStatisticsProjectReportExportJobDownload(jobId)
            if (!result.success || !result.download) {
                message.error(result.errorMessage || t('exportTasks.downloadFailed'))
                return
            }
            triggerStatisticsProjectReportExportDownload(result.download)
            message.success(t('exportTasks.downloadStarted'))
        },
        [t]
    )

    useEffect(() => {
        if (!activeExportJobId) {
            return
        }
        let cancelled = false
        const pollExportJob = async () => {
            const result = await getStatisticsProjectReportExportJob(activeExportJobId)
            if (cancelled) {
                return
            }
            if (!result.success || !result.job) {
                setActiveExportJobId(null)
                message.error(result.errorMessage || t('exportTasks.statusFailed'))
                void refreshExportJobs()
                return
            }
            const job = result.job
            setExportJobs((current) => [
                job,
                ...current.filter((item) => item.id !== job.id),
            ])
            if (job.status === 'queued' || job.status === 'processing') {
                return
            }
            setActiveExportJobId(null)
            if (job.status === 'completed' && job.download_ready) {
                await handleExportJobDownload(job.id)
                return
            }
            if (job.status === 'expired') {
                message.error(t('exportTasks.expired'))
                return
            }
            message.error(job.error_message || t('export.failed'))
        }

        void pollExportJob()
        const timer = window.setInterval(() => {
            void pollExportJob()
        }, 2500)
        return () => {
            cancelled = true
            window.clearInterval(timer)
        }
    }, [activeExportJobId, handleExportJobDownload, refreshExportJobs, t])

    const updateExportFilter = useCallback(
        <K extends keyof StatisticsFilters>(
            filterKey: K,
            values: StatisticsFilters[K]
        ) => {
            setExportFilters((previousFilters) => {
                if (
                    areArraysEqual(
                        previousFilters[filterKey] as Array<number | string>,
                        values as Array<number | string>
                    )
                ) {
                    return previousFilters
                }

                return {
                    ...previousFilters,
                    [filterKey]: values,
                }
            })
        },
        []
    )

    const getExportJobTagColor = (status: StatisticsExportJobStatus) => {
        if (status === 'completed') return 'green'
        if (status === 'failed' || status === 'expired') return 'red'
        if (status === 'processing') return 'blue'
        return 'default'
    }
    const formatDateTimeText = (value?: string | null) =>
        value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-'
    const formatExportJobProgress = (job: StatisticsExportJob) =>
        t('exportTasks.progress', {
            processed: job.processed_projects,
            total: job.total_projects,
        })
    const renderExportJobFilters = (job: StatisticsExportJob) => {
        const filters = job.filters
        if (!filters) {
            return renderPaginatedListText(null)
        }

        const allProjectLabel = t('filters.allProject')
        const allRegionLabel = t('filters.allRegion')
        const allDeviceLabel = t('filters.allDevice')
        const allPestTypeLabel = t('filters.allPestType')
        const filterRows = [
            {
                key: 'date_range',
                label: t('exportTasks.filters.dateRange', {
                    defaultValue: '日期范围',
                }),
                value: filters.date_range,
            },
            {
                key: 'project',
                label: t('exportTasks.filters.project', {
                    defaultValue: '项目',
                }),
                value: filters.project || allProjectLabel,
            },
            {
                key: 'region',
                label: t('exportTasks.filters.region', {
                    defaultValue: '地区',
                }),
                value: filters.region || allRegionLabel,
            },
            {
                key: 'device',
                label: t('exportTasks.filters.device', {
                    defaultValue: '设备',
                }),
                value: filters.device || allDeviceLabel,
            },
            {
                key: 'pest_type',
                label: t('exportTasks.filters.pestType', {
                    defaultValue: '蚊虫类型',
                }),
                value: filters.pest_type || allPestTypeLabel,
            },
        ]

        return (
            <div className="statistics-export-task-filters">
                {filterRows.map((row) => (
                    <div
                        className="statistics-export-task-filter-row"
                        key={row.key}
                    >
                        <Typography.Text
                            className="statistics-export-task-filter-label"
                            strong
                        >
                            {row.label}：
                        </Typography.Text>
                        <Typography.Text className="statistics-export-task-filter-value">
                            {row.value}
                        </Typography.Text>
                    </div>
                ))}
            </div>
        )
    }

    const exportJobColumns: ColumnsType<StatisticsExportJob> = [
        {
            title: t('exportTasks.columns.createdAt', { defaultValue: '创建时间' }),
            dataIndex: 'created_at',
            key: 'createdAt',
            width: 150,
            render: (value?: string | null) =>
                renderPaginatedListText(formatDateTimeText(value)),
        },
        {
            title: t('exportTasks.columns.filters', { defaultValue: '导出条件' }),
            key: 'filters',
            width: 324,
            render: (_, job) => renderExportJobFilters(job),
        },
        {
            title: t('exportTasks.columns.progress', { defaultValue: '处理进度' }),
            key: 'progress',
            width: 132,
            render: (_, job) => (
                <Space direction="vertical" size={2}>
                    <Typography.Text>
                        {formatExportJobProgress(job)}
                    </Typography.Text>
                    {job.error_message && (
                        <Typography.Text type="danger">
                            {job.error_message}
                        </Typography.Text>
                    )}
                </Space>
            ),
        },
        {
            title: t('exportTasks.columns.status', { defaultValue: '状态' }),
            dataIndex: 'status',
            key: 'status',
            width: 82,
            render: (status: StatisticsExportJobStatus) => (
                <Tag color={getExportJobTagColor(status)}>
                    {t(`exportTasks.status.${status}`)}
                </Tag>
            ),
        },
        {
            title: t('exportTasks.columns.actions', { defaultValue: '操作' }),
            key: 'actions',
            width: 78,
            fixed: 'right' as const,
            render: (_, job) =>
                job.download_ready ? (
                    <Button
                        size="small"
                        onClick={() => {
                            void handleExportJobDownload(job.id)
                        }}
                    >
                        {t('exportTasks.download')}
                    </Button>
                ) : (
                    renderPaginatedListText(null)
                ),
        },
    ]

    const exportProjectOptionsForModalSelectOptions = buildAllFilterOptions(
        t('filters.allProject'),
        exportProjectSelectOptions
    )
    const exportRegionOptionsForModal = buildAllFilterOptions(
        t('filters.allRegion'),
        exportRegionSelectOptions
    )
    const exportDeviceOptionsForModal = buildAllFilterOptions(
        t('filters.allDevice'),
        exportDeviceSelectOptions
    )
    const exportPestTypeOptionsForModal = buildAllFilterOptions(
        t('filters.allPestType'),
        exportDisplayPestTypeOptions
    )

    const exportTaskListModal = canExportStatistics && exportJobsVisible ? (
        <Modal
            className="statistics-export-task-modal"
            title={t('exportTasks.title')}
            open={exportJobsVisible}
            onCancel={() => setExportJobsVisible(false)}
            footer={[
                <Button
                    key="refresh"
                    icon={<ReloadOutlined />}
                    onClick={() => {
                        void refreshExportJobs()
                    }}
                    loading={exportJobsLoading}
                >
                    {t('exportTasks.refresh')}
                </Button>,
                <Button key="close" onClick={() => setExportJobsVisible(false)}>
                    {t('exportTasks.close')}
                </Button>,
            ]}
            width={972}
        >
            <Space
                className="statistics-export-task-content"
                direction="vertical"
                size={12}
                style={{ width: '100%' }}
            >
                <Alert type="info" showIcon message={t('exportTasks.hint')} />
                {exportJobsError && (
                    <Alert type="error" showIcon message={exportJobsError} />
                )}
                <PaginatedListCard className="statistics-export-task-list">
                    <Table
                        columns={exportJobColumns}
                        dataSource={exportJobs}
                        rowKey="id"
                        size={PAGINATED_LIST_TABLE_SIZE}
                        tableLayout="fixed"
                        loading={exportJobsLoading}
                        locale={{ emptyText: t('exportTasks.empty') }}
                        scroll={{ x: 800 }}
                        pagination={{
                            ...buildPaginatedListPagination({
                                current: exportJobsPage,
                                pageSize: exportJobsPageSize,
                                total: exportJobs.length,
                                size: 'small',
                                showTotal: (total) =>
                                    t('exportTasks.pagination.total', {
                                        total,
                                        defaultValue: '共 {{total}} 个任务',
                                    }),
                            }),
                            pageSizeOptions: EXPORT_TASKS_PAGE_SIZE_OPTIONS,
                            onChange: (nextPage, nextPageSize) => {
                                if (
                                    nextPageSize &&
                                    nextPageSize !== exportJobsPageSize
                                ) {
                                    setExportJobsPageSize(nextPageSize)
                                    setExportJobsPage(1)
                                    return
                                }
                                setExportJobsPage(nextPage)
                            },
                        }}
                    />
                </PaginatedListCard>
            </Space>
        </Modal>
    ) : null

    const pageTitleConfig = getAnalysisPageTitleKey(lockedChartType)
    const pageTitle = t(pageTitleConfig.titleKey, {
        defaultValue: pageTitleConfig.defaultTitle,
    })
    const currentStatisticsScopeDeviceOptions =
        statisticsScopeDeviceOptions.filter((device) =>
            filters.deviceIds.length > 0
                ? filters.deviceIds.includes(device.id)
                : true
        )
    const configuredStatisticsScopeDeviceOptions =
        currentStatisticsScopeDeviceOptions.filter(hasDisplayPurchaseVersion)
    const currentStatisticsScopeVersionSummary = buildVersionCountSummary(
        configuredStatisticsScopeDeviceOptions,
        t
    )
    const hasSupportedDevicesForDedicatedPage =
        !isDedicatedAnalysisPage || featureScopedDeviceOptions.length > 0
    const shouldShowUnsupportedVersion =
        isDedicatedAnalysisPage &&
        !summaryLoading &&
        !hasSupportedDevicesForDedicatedPage
    const applicableDeviceCount = currentScopedDeviceOptions.filter((device) =>
        hasConfiguredCapability(device, currentAnalysisFeature)
    ).length
    const shouldShowNoResultsForDedicatedPage =
        isDedicatedAnalysisPage && !summaryLoading && applicableDeviceCount === 0
    const applicableVersionNames = useMemo(() => {
        if (!isDedicatedAnalysisPage) {
            return undefined
        }

        return getFixedApplicableVersionNames(activeChartType, t)
    }, [activeChartType, isDedicatedAnalysisPage, t])
    const analysisHeader = isDedicatedAnalysisPage ? (
        <Card className="statistics-analysis-hero">
            <div className="statistics-analysis-hero-main">
                <div>
                    <div className="statistics-analysis-title-row">
                        <Title level={2} className="statistics-analysis-title">
                            {pageTitle}
                        </Title>
                        <Text className="statistics-analysis-scope-pill">
                            {t('analysisHeader.deviceScope', {
                                count: configuredStatisticsScopeDeviceOptions.length,
                                packages: currentStatisticsScopeVersionSummary,
                                defaultValue: `共${configuredStatisticsScopeDeviceOptions.length}台设备，${currentStatisticsScopeVersionSummary}`,
                            })}
                        </Text>
                    </div>
                    <Space wrap size={[8, 8]} className="statistics-analysis-meta">
                        <Tag>
                            {t('analysisHeader.updatedAt', {
                                time: dayjs().format('YYYY/MM/DD HH:mm'),
                                defaultValue: `Updated at ${dayjs().format('YYYY/MM/DD HH:mm')}`,
                            })}
                        </Tag>
                        <Tag color="blue">
                            {t('analysisHeader.applicableVersions', {
                                versions: applicableVersionNames || '-',
                                defaultValue: `Applicable versions: ${applicableVersionNames || '-'}`,
                            })}
                        </Tag>
                        <Tag color="green">
                            {t('analysisHeader.applicableDeviceCount', {
                                count: applicableDeviceCount,
                                defaultValue: `统计设备数量：${applicableDeviceCount}台`,
                            })}
                        </Tag>
                    </Space>
                </div>
            </div>
        </Card>
    ) : null

    const statisticsBody = (
        <>
            {analysisHeader}
            <StatisticsHeader
                dateRange={dateRange}
                chartType={activeChartType}
                activeQuickRangeKey={
                    requestedDateRange === null ? 'all' : activeQuickRangeKey
                }
                selectedProjectIds={filters.projectIds}
                selectedRegionIds={filters.regionIds}
                selectedDeviceIds={filters.deviceIds}
                selectedPestTypes={filters.pestTypes}
                projectOptions={filterProjectOptions.map((project) => ({
                    value: project.id,
                    label: project.label,
                }))}
                regionOptions={filterRegionOptions.map((region) => ({
                    value: region.id,
                    label: region.label,
                }))}
                deviceOptions={availableDeviceOptions.map((device) => ({
                    value: device.id,
                    label: device.label,
                }))}
                pestTypeOptions={displayPestTypeOptions}
                onDateRangeChange={handleDateRangeChange}
                onChartTypeChange={handleChartTypeChange}
                onQuickRangeChange={handleQuickRangeChange}
                onProjectChange={(values) => updateFilter('projectIds', values)}
                onRegionChange={(values) => updateFilter('regionIds', values)}
                onDeviceChange={(values) => updateFilter('deviceIds', values)}
                onPestTypeChange={(values) => updateFilter('pestTypes', values)}
                onResetFilters={handleResetFilters}
                onRefresh={handleRefresh}
                onExport={handleOpenExportModal}
                onExportTasks={handleOpenExportJobs}
                hasActiveFilters={hasActiveFilters}
                showExport={canExportStatistics}
                exportDisabled={false}
                exportDisabledReason={exportDisabledReason}
                exporting={exporting}
                showChartTypeSelector={!isDedicatedAnalysisPage}
            />

            <div className="statistics-chart-content">
                {summaryLoading ? (
                    <Card>
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Spin size="large" />
                        </div>
                    </Card>
                ) : summaryError ? (
                    <Card>
                        <Alert
                            type="error"
                            showIcon
                            message={summaryErrorMessage}
                            action={
                                <Button type="primary" onClick={handleRefresh}>
                                    {t('common:buttons.retry')}
                                </Button>
                            }
                        />
                    </Card>
                ) : shouldShowUnsupportedVersion ? (
                    <Card>
                        <Empty
                            description={t('empty.unsupportedVersion', {
                                defaultValue: '当前版本不支持',
                            })}
                        />
                    </Card>
                ) : (
                    <>
                        {statisticsData && (
                            <>
                                {!isDedicatedAnalysisPage && (
                                    <StatisticsSummary
                                        statisticsData={statisticsData}
                                        versionName={speciesRecognitionVersionName}
                                    />
                                )}

                                {isChartLoading ? (
                                    <Card>
                                        <div
                                            style={{
                                                textAlign: 'center',
                                                padding: '40px',
                                            }}
                                        >
                                            <Spin size="large" />
                                        </div>
                                    </Card>
                                ) : (
                                    <>
                                        {shouldShowNoResultsForDedicatedPage ? (
                                            <Card>
                                                <Empty
                                                    description={t(
                                                        'empty.noResults'
                                                    )}
                                                />
                                            </Card>
                                        ) : chartData && isEmptyResult ? (
                                            <Card>
                                                <Empty
                                                    description={t(
                                                        'empty.noResults'
                                                    )}
                                                />
                                            </Card>
                                        ) : chartData ? (
                                            renderDedicatedChartArea()
                                        ) : null}
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </>
    )

    function renderDedicatedChartArea() {
        if (!chartData) {
            return null
        }

        if (isDedicatedAnalysisPage && activeChartType === 'distribution') {
            const speciesSummary = getSpeciesSummaryForChart(chartData)
            const comparisonSummary = buildComparisonCardData(speciesSummary)
            const previousComparisonSummary = buildComparisonCardData(
                comparisonData.previous?.speciesSummary
            )

            if (!speciesSummary.pestDistribution?.length) {
                return (
                    <Card>
                        <Empty description={t('empty.noDistributionData')} />
                    </Card>
                )
            }

            return (
                <Row gutter={[16, 16]} className="statistics-species-analysis-grid">
                    <Col xs={24} xl={11}>
                        <DistributionChart data={speciesSummary.pestDistribution} />
                    </Col>
                    <Col xs={24} xl={13}>
                        <ComparisonAnalysis
                            currentData={{
                                total: comparisonSummary.total,
                                byType: comparisonSummary.byType,
                            }}
                            previousData={{
                                total: previousComparisonSummary.total,
                                byType: previousComparisonSummary.byType,
                            }}
                        />
                    </Col>
                </Row>
            )
        }

        return (
            <StatisticsChartArea
                chartType={activeChartType}
                statisticsData={chartData}
                comparisonData={comparisonData}
                versionName={dataReportsVersionName}
                trendComparisonWindowDays={trendComparisonWindowDays}
                businessDate={businessDate}
            />
        )
    }

    return (
        <PageLayout
            title={pageTitle}
            framed={isDedicatedAnalysisPage}
            breadcrumb={[
                { title: t('navigation:breadcrumb.dataCenter') },
                {
                    title:
                        lockedChartType === undefined
                            ? t('navigation:breadcrumb.statistics')
                            : pageTitle,
                },
            ]}
        >
            {isDedicatedAnalysisPage ? (
                <section className="statistics-dedicated-shell">
                    {statisticsBody}
                </section>
            ) : (
                statisticsBody
            )}

            <Modal
                open={exportModalOpen}
                title={t('export.modalTitle')}
                onCancel={() => setExportModalOpen(false)}
                onOk={() => void handleExport()}
                okText={t('export.confirm')}
                cancelText={t('export.cancel')}
                confirmLoading={exporting}
                okButtonProps={{ disabled: !canConfirmExport || exporting }}
                footer={(_, { OkBtn, CancelBtn }) => (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            flexWrap: 'wrap',
                            gap: 8,
                        }}
                    >
                        <Space size={8} wrap>
                            <CancelBtn />
                            <OkBtn />
                        </Space>
                    </div>
                )}
                width={720}
                maskClosable={false}
                destroyOnClose={false}
            >
                <Spin spinning={exportOptionsLoading}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: 8 }}>
                                {t('filters.dateRange')}
                            </div>
                            <ReadonlyRangePicker
                                style={{ width: '100%' }}
                                value={exportDateRange}
                                allowClear={false}
                                onChange={(dates) => {
                                    if (!dates || !dates[0] || !dates[1]) {
                                        return
                                    }

                                    setExportDateRange(dates as [Dayjs, Dayjs])
                                }}
                            />
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: 8 }}>
                                {t('filters.project')}
                            </div>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                style={{ width: '100%' }}
                                value={getAllAwareDisplayValues(
                                    exportFilters.projectIds
                                )}
                                options={exportProjectOptionsForModalSelectOptions}
                                placeholder={t('filters.projectPlaceholder')}
                                optionFilterProp="label"
                                maxTagCount="responsive"
                                onChange={(value) =>
                                    updateExportFilter(
                                        'projectIds',
                                        normalizeAllAwareValues(
                                            value,
                                            exportFilters.projectIds
                                        )
                                    )
                                }
                            />
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: 8 }}>
                                {t('filters.region')}
                            </div>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                style={{ width: '100%' }}
                                value={getAllAwareDisplayValues(
                                    exportFilters.regionIds
                                )}
                                options={exportRegionOptionsForModal}
                                placeholder={t('filters.regionPlaceholder')}
                                optionFilterProp="label"
                                filterOption={filterRegionSelectOption}
                                maxTagCount="responsive"
                                onChange={(value) =>
                                    updateExportFilter(
                                        'regionIds',
                                        normalizeAllAwareValues(
                                            value,
                                            exportFilters.regionIds
                                        )
                                    )
                                }
                            />
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: 8 }}>
                                {t('filters.device')}
                            </div>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                style={{ width: '100%' }}
                                value={getAllAwareDisplayValues(
                                    exportFilters.deviceIds
                                )}
                                options={exportDeviceOptionsForModal}
                                placeholder={t('filters.devicePlaceholder')}
                                optionFilterProp="label"
                                maxTagCount="responsive"
                                onChange={(value) =>
                                    updateExportFilter(
                                        'deviceIds',
                                        normalizeAllAwareValues(
                                            value,
                                            exportFilters.deviceIds
                                        )
                                    )
                                }
                            />
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: 8 }}>
                                {t('filters.pestType')}
                            </div>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                style={{ width: '100%' }}
                                value={getAllAwareDisplayValues(
                                    exportFilters.pestTypes
                                )}
                                options={exportPestTypeOptionsForModal}
                                placeholder={t('filters.pestTypePlaceholder')}
                                optionFilterProp="label"
                                maxTagCount="responsive"
                                onChange={(value) =>
                                    updateExportFilter(
                                        'pestTypes',
                                        normalizeAllAwareValues(
                                            value,
                                            exportFilters.pestTypes
                                        )
                                    )
                                }
                            />
                        </Col>
                    </Row>
                </Spin>
            </Modal>
            {exportTaskListModal}
        </PageLayout>
    )
}

export default Statistics
